/**
 * $2     60px
 * $2.1   75px
 * $22    70px
 * $2.2   75px
 *
 * 40 + (10 * amountOfLetter) - 5 (if one of the letters is a comma (or point))
 *
 * Our number formatter for charts need some work
 * 1. Current price
 *    We want a tick for the current price (and maybe a different color (but sublte))
 *
 * 2. Meaningful ticks
 *    If we have channels each max/min of the bands can be a tick
 *    Same for the max high, min low
 *    We might be able to use the non overlap key
 *      If not, try your best to find a way to have no overlaps
 *    We can also give ticks to transaction ordering prices
 *
 * 3. Filling the space between
 *    Calculate the range between the ticks compared to the domain
 *    And see how many ticks we can fill
 *
 * 4. Formatting
 *    x < 0       =>  4 decimals
 *    1 < x < 2   =>  4 decimals
 *    2 < x < 10  =>  2 decimals
 *    10 < x      =>  no decimals
 */

import moment from 'moment';
import { DomainTuple } from 'victory';
import { isZoningBand } from '../../components/Charts/CandleSticks';
import { Candle } from '../../screens/Detail/types';
import { Transaction } from '../../screens/Overview/types';
import {
  Band,
  CompactTransaction as SPCCompactTransaction,
  IndicatorSingleValue,
  ZoningBand,
} from '../../screens/ProfitCalculations/types';
import { formatPrice } from '../NumberFormatter';
import { CompactTransaction } from '../../types/subscription';
import { GenericTransactionInfo } from '../../types/Transaction';

export type DataPoint = {
  low: number;
  open: number;
  close: number;
  high: number;
  x: number;
  x2: number;
  y?: number;
};

export const formatToCandlestickDataPointType = (
  candle: Candle,
): DataPoint => ({
  low: candle.low,
  open: candle.open,
  close: candle.close,
  high: candle.high,
  x: candle.startTimestamp,
  x2: candle.endTimestamp,
  y: candle.close,
});

export const createYAxisCandlesticksTicks = (
  dataPoints: DataPoint[],
  transactions:
    | CompactTransaction[]
    | SPCCompactTransaction[]
    | Transaction[]
    | GenericTransactionInfo[]
    | undefined,
  channel: Band | ZoningBand | undefined,
  domain: {
    x: DomainTuple;
    y: DomainTuple;
  },
  maxDecimals?: number,
) => {
  if (!dataPoints?.length) {
    return [];
  }

  const domainRange = (domain.y[1] as number) - (domain.y[0] as number);
  const ticks: number[] = [];

  const pushToTicks = (possibleTick: number | null) => {
    if (!possibleTick) {
      return;
    }

    /** We want to check if the tick is too close to an other one */

    for (let index = 0; index < ticks.length; index++) {
      const tick = ticks[index];
      const range = [tick - domainRange * 0.05, tick + domainRange * 0.05];

      if (range[0] <= possibleTick && possibleTick <= range[1]) {
        return;
      }
    }

    ticks.push(possibleTick);
  };

  /** 1. Transactions */
  transactions?.forEach((transaction) => {
    pushToTicks(transaction.orderPrice || 0);
  });

  /** 2. The main tick is the latest close */
  const latestClose = dataPoints[dataPoints.length - 1].close;
  pushToTicks(latestClose);

  /** 3. Meaningful ticks */
  /** 3.1 Min/max candle prices */
  const maxHigh = Math.max(...dataPoints.map((c) => c.high));
  const minLow = Math.min(...dataPoints.map((c) => c.low));
  const maxClose = Math.max(...dataPoints.map((c) => c.close));
  const minClose = Math.min(...dataPoints.map((c) => c.close));
  pushToTicks(maxHigh);
  pushToTicks(minLow);
  pushToTicks(maxClose);
  pushToTicks(minClose);

  /** 3.2 Channels
   * Max of sell/upper band
   * Max and min of middle band
   * Min of buy/lower
   */
  const filterChannel = (band: IndicatorSingleValue[]) =>
    band.filter(
      (c) =>
        (domain.x[0] as number) <= c.timestamp &&
        c.timestamp <= (domain.x[1] as number),
    );

  const currentSellBandValue =
    channel && isZoningBand(channel)
      ? channel.sellBand[channel.sellBand.length - 1]?.value
      : null;
  const currentUpperBandValue = channel
    ? channel.upperBand[channel.upperBand.length - 1]?.value
    : null;
  const currentMiddleBandValue = channel
    ? channel.upperBand[channel.middleBand.length - 1]?.value
    : null;
  const currentLowerBandValue = channel
    ? channel.upperBand[channel.lowerBand.length - 1]?.value
    : null;
  const currentBuyBandValue =
    channel && isZoningBand(channel)
      ? channel.buyBand[channel.buyBand.length - 1]?.value
      : null;

  pushToTicks(currentUpperBandValue);
  pushToTicks(currentLowerBandValue);
  pushToTicks(currentSellBandValue);
  pushToTicks(currentBuyBandValue);
  pushToTicks(currentMiddleBandValue);

  const maxSellBandValue =
    channel && isZoningBand(channel) && filterChannel(channel.sellBand).length
      ? Math.max(...filterChannel(channel.sellBand).map((c) => c.value))
      : null;
  const maxUpperBandValue =
    channel && filterChannel(channel.upperBand).length
      ? Math.max(...filterChannel(channel.upperBand).map((c) => c.value))
      : null;
  const maxMiddleBandValue =
    channel && filterChannel(channel.middleBand).length
      ? Math.max(...filterChannel(channel.middleBand).map((c) => c.value))
      : null;
  const minMiddleBandValue =
    channel && filterChannel(channel.middleBand).length
      ? Math.min(...filterChannel(channel.middleBand).map((c) => c.value))
      : null;
  const minLowerBandValue =
    channel && filterChannel(channel.lowerBand).length
      ? Math.min(...filterChannel(channel.lowerBand).map((c) => c.value))
      : null;
  const minBuyBandValue =
    channel && isZoningBand(channel) && filterChannel(channel.buyBand).length
      ? Math.min(...filterChannel(channel.buyBand).map((c) => c.value))
      : null;

  pushToTicks(maxUpperBandValue);
  pushToTicks(minLowerBandValue);
  pushToTicks(maxSellBandValue);
  pushToTicks(minBuyBandValue);
  pushToTicks(maxMiddleBandValue);
  pushToTicks(minMiddleBandValue);

  /** Fill the space in between
   * Loop over each tick
   * Calculat the range to the next one
   * If the range is big enough,
   * want to add extra ticks
   *
   * Divide the range by a certain height
   * and round down
   * This is the amount of ticks to add
   */

  const maxLabelArea = domainRange * 0.12;

  /** Sort the tick from high to low */
  ticks.sort((a, b) => b - a);

  const fillerTicks = [];

  for (let index = 0; index < ticks.length; index++) {
    const tick = ticks[index];
    let startOfRange, range, amountOfTicks;

    if (index === 0) {
      /** First */
      startOfRange = domain.y[1] as number;
      range = startOfRange - tick;
      amountOfTicks = Math.floor(range / maxLabelArea);
    } else if (index === ticks.length - 1) {
      /** Last */
      startOfRange = tick;
      const domainMinY = domain.y[0] as number;
      range = tick - domainMinY;
      amountOfTicks = Math.floor(range / maxLabelArea);
    } else {
      startOfRange = tick;
      const nextTick = ticks[index + 1];
      range = tick - nextTick;
      amountOfTicks = Math.floor(range / maxLabelArea);
    }

    if (amountOfTicks < 1) {
      continue;
    }

    const tickOffset = range / (amountOfTicks + 1);

    for (let j = 0; j < amountOfTicks; j++) {
      fillerTicks.push(startOfRange - tickOffset * (j + 1));
    }
  }

  fillerTicks.forEach((fillerTick) => {
    pushToTicks(fillerTick);
  });

  /** For each tick, we want to round the the nearest 0 or 5 value
   * For example:
   * 3.16 => 3.15
   * 3.18 => 3.20
   * 3.152 => 3.150
   * 3.128 => 3.130
   * 3.1574 => 3.1575
   */

  for (let index = 0; index < ticks.length; index++) {
    const tick = parseFloat(
      ticks[index].toFixed(calculatePrecision(ticks[index])),
    );
    /** Pre-format */
    ticks[index] = tick;

    const integerValue = Math.trunc(tick);
    const decimals = parseFloat(
      (tick - integerValue).toFixed(calculatePrecision(ticks[index])),
    );

    if (decimals === 0) {
      /** This tick has no decimals */
      continue;
    }

    if (decimals.toString().length < 4) {
      /** Don't do anything for 1 decimal */
      continue;
    }

    const decimalPower = Math.pow(10, decimals.toString().length - 2);

    let fullDecimalNumber = Math.round(decimals * decimalPower);

    const lastNumber = fullDecimalNumber.toString().slice(-1);
    const lastNumberValue = parseInt(lastNumber);

    if (lastNumberValue === 0 || lastNumberValue === 5) {
      continue;
    }

    if (0 < lastNumberValue && lastNumberValue <= 2) {
      fullDecimalNumber = fullDecimalNumber - lastNumberValue;
    } else if (2 < lastNumberValue && lastNumberValue <= 4) {
      fullDecimalNumber = fullDecimalNumber + (5 - lastNumberValue);
    } else if (5 < lastNumberValue && lastNumberValue <= 7) {
      fullDecimalNumber = fullDecimalNumber - (lastNumberValue - 5);
    } else {
      fullDecimalNumber = fullDecimalNumber + (10 - lastNumberValue);
    }

    const fullUpdatedNumber = integerValue + fullDecimalNumber / decimalPower;
    ticks[index] = fullUpdatedNumber;
  }

  return ticks;
};

const calculatePrecision = (t: number) => {
  let precision = 0;

  if (Math.abs(t) < 1) {
    precision = 7;
  }

  if (1 <= Math.abs(t) && Math.abs(t) <= 10) {
    precision = 2;
  }

  if (10 <= Math.abs(t)) {
    precision = 0;
  }

  return precision;
};

export const formatYTick = (t: number) => {
  const precision = calculatePrecision(t);
  return `${formatPrice(t, true, precision, true)}`;
};

/** A horizontal annotation line drawn over a candlestick chart (a cbrt range
 * break or an MRC phase). Each line is two points sharing the same y. */
export interface OverlayLine {
  points: Array<{ x: number; y: number }>;
  color: string;
  label?: string;
  strokeWidth: number;
  labelDy: number;
}

export const calculateYTicks = (
  data: { x: number; y?: number }[] | undefined,
  forceZero = false,
): number[] => {
  if (!data?.length) return [];

  // Find min and max
  const values = data.map((d) => d.y ?? 0);
  const min = Math.min(...values);
  const max = Math.max(...values);

  // Determine if we cross zero or force zero
  const hasPositive = max > 0;
  const hasNegative = min < 0;
  const includeZero =
    (hasPositive && hasNegative) || (forceZero && hasPositive && !hasNegative);

  // Calculate initial range and rough step
  const range = max - min;
  const targetTicks = 8;
  let step = range / (targetTicks - 1);

  if (step < 0.01) {
    const average = data.reduce((sum, d) => sum + (d.y || 0), 0) / data.length;
    return [
      average - 0.02,
      average - 0.01,
      average,
      average + 0.01,
      average + 0.02,
    ];
  }

  // Find a "pretty" step size
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(step))));
  let stepBase = step / magnitude;

  // Round to nearest "pretty" number
  if (stepBase < 1.5) stepBase = 1;
  else if (stepBase < 3) stepBase = 2;
  else if (stepBase < 7) stepBase = 5;
  else stepBase = 10;

  step = stepBase * magnitude;

  // Calculate pretty min and max
  let prettyMin = Math.floor(min / step) * step;
  let prettyMax = Math.ceil(max / step) * step;

  // Adjust based on presence of negative/positive values
  if (hasPositive && hasNegative) {
    // If both positive and negative, adjust min and max independently
    const stepsBelowZero = Math.ceil(Math.abs(min) / step);
    prettyMin = -stepsBelowZero * step;
    const stepsAboveZero = Math.ceil(max / step);
    prettyMax = stepsAboveZero * step;
  } else if (includeZero && !hasNegative) {
    prettyMin = 0;
    const stepsAboveZero = Math.ceil(prettyMax / step);
    prettyMax = stepsAboveZero * step;
  }

  // Generate ticks
  const ticks: number[] = [];
  let current = prettyMin;
  while (current <= prettyMax + 1e-10) {
    ticks.push(Math.round(current * 100) / 100);
    current += step;
  }

  // Ensure 5 or 6 ticks
  if (ticks.length > 6) {
    step *= 2;
    prettyMin = Math.floor(min / step) * step;
    prettyMax = Math.ceil(max / step) * step;
    if (hasPositive && hasNegative) {
      const stepsBelowZero = Math.ceil(Math.abs(min) / step);
      prettyMin = -stepsBelowZero * step;
      const stepsAboveZero = Math.ceil(max / step);
      prettyMax = stepsAboveZero * step;
    } else if (includeZero && !hasNegative) {
      prettyMin = 0;
      const stepsAboveZero = Math.ceil(prettyMax / step);
      prettyMax = stepsAboveZero * step;
    }
    ticks.length = 0;
    current = prettyMin;
    while (current <= prettyMax + 1e-10) {
      ticks.push(Math.round(current * 100) / 100);
      current += step;
    }
  }

  return ticks;
};

export const calculateLongestTickWidth = (
  yTicks: number[] | undefined,
  yTickFormatter: (value: number) => string,
) => {
  if (!yTicks?.length) {
    return 0;
  }

  const tickLength = yTicks.map((y) => yTickFormatter(y).length);
  const maxLength = Math.max(...tickLength);
  return maxLength * 7;
};

// We want to calculate the longest tick label and size the y axis accordingly
export const calculateYAxisWidth = (longestTickWidth: number) => {
  return Math.max(60, longestTickWidth + 30);
};

// This might look like some magic
// But the most important part is the bigger yAxisWidth is, the smaller the tickSize should be (last part of the equation)
export const calculateTickSize = (
  yAxisWidth: number,
  longestTickWidth: number,
) => (yAxisWidth - longestTickWidth) * (0.55 - 10 / yAxisWidth);

export const calculateYTickFormatterDecimals = (yTicks: number[]) => {
  if (!yTicks?.length) {
    return 0;
  }

  // If the step between ticks is smaller than 1, show 2 decimals
  const step = (yTicks[1] ?? 0) - (yTicks[0] ?? 0);

  if (step < 1) {
    return 2;
  }

  // Now for bigger numbers, the way we use the formatter, it's actually the first two numbers that counts
  // If at some point the 2 first numbers are the same for any ticks, we need to show 1 decimal (in case we would show 20k twice for example)
  const sameNumbers = yTicks.some((tick, index) => {
    const secondTick = yTicks[index + 1];

    if (!secondTick) {
      return false;
    }

    const firstNumbers = Math.abs(tick).toString().slice(0, 2);
    const secondNUmbers = Math.abs(secondTick).toString().slice(0, 2);
    return firstNumbers === secondNUmbers;
  });

  return sameNumbers ? 1 : 0;
};

// The shared y-domain maths lives in its own module (no component imports, so it is unit
// testable) and is re-exported here — every existing caller keeps importing it from
// `helpers/Chart`.
export {
  sharedDomainScale,
  stretchYValueOnSharedDomain,
  niceLinearTicks,
} from './sharedDomain';

/* ===================== Adaptive time axis ===================== */

export type TimeAxisUnit = 'year' | 'quarter' | 'month' | 'week';

/** One interval (clipped to the visible domain) of the adaptive time axis. */
export interface TimeAxisInterval {
  /** Visible start — equals the domain start for the first interval. */
  startTs: number;
  /** Visible end — equals the domain end for the last interval. */
  endTs: number;
  label: string;
}

const TIME_AXIS_DAY_MS = 24 * 60 * 60 * 1000;

/** Pick the granularity from the data span — long back-tests get years, very
 * short ones get weeks. */
const pickTimeAxisUnit = (durationMs: number): TimeAxisUnit => {
  if (durationMs >= 3 * 365 * TIME_AXIS_DAY_MS) return 'year';
  if (durationMs >= 365 * TIME_AXIS_DAY_MS) return 'quarter';
  if (durationMs >= 60 * TIME_AXIS_DAY_MS) return 'month';
  return 'week';
};

const TIME_AXIS_LABEL_FORMAT: Record<TimeAxisUnit, string> = {
  year: 'YYYY',
  quarter: '[Q]Q YYYY',
  month: 'MMM YYYY',
  week: 'DD MMM',
};

/**
 * Split a time domain into calendar intervals — the basis for both the chart's
 * vertical gridlines and the aligned x-axis footer. The granularity adapts to
 * the span (year → quarter → month → week) and every interval is clipped to
 * the domain, so the set tiles [domainStart, domainEnd] exactly.
 */
export const buildTimeAxis = (
  domainStart: number,
  domainEnd: number,
): { unit: TimeAxisUnit; intervals: TimeAxisInterval[] } => {
  if (
    !Number.isFinite(domainStart) ||
    !Number.isFinite(domainEnd) ||
    domainEnd <= domainStart
  ) {
    return { unit: 'year', intervals: [] };
  }

  const unit = pickTimeAxisUnit(domainEnd - domainStart);
  const startOfUnit = unit === 'week' ? 'isoWeek' : unit;
  const intervals: TimeAxisInterval[] = [];

  let naturalStart = moment(domainStart).startOf(startOfUnit);
  // A hard ceiling so a malformed domain can never spin the loop forever.
  for (let guard = 0; guard < 1000; guard++) {
    if (naturalStart.valueOf() >= domainEnd) {
      break;
    }
    const naturalEnd = naturalStart.clone().add(1, unit);
    intervals.push({
      startTs: Math.max(naturalStart.valueOf(), domainStart),
      endTs: Math.min(naturalEnd.valueOf(), domainEnd),
      label: moment(naturalStart.valueOf()).format(
        TIME_AXIS_LABEL_FORMAT[unit],
      ),
    });
    naturalStart = naturalEnd;
  }

  return { unit, intervals };
};
